import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Database, Users, CreditCard, MessageCircle, Mail,
  Utensils, CheckCircle, XCircle, Settings, RefreshCw, Play,
  Plug, ExternalLink, Clock, AlertTriangle, Building2, ShoppingCart,
  UserCheck, Package, Inbox
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import LanguageSwitcher from '../components/LanguageSwitcher';
import IncomingOrders1C from './IncomingOrders1C';

function AdminIntegrations({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('bitrix24');
  const [loading, setLoading] = useState(true);
  
  // Bitrix24 state
  const [bitrixStatus, setBitrixStatus] = useState(null);
  const [bitrixTesting, setBitrixTesting] = useState(false);
  const [bitrixSyncing, setBitrixSyncing] = useState(null);
  const [syncStats, setSyncStats] = useState(null);
  
  // 1C state
  const [oneCData, setOneCData] = useState(null);
  const [oneCLoading, setOneCLoading] = useState(false);
  
  // Other integrations (stubs)
  const [integrations, setIntegrations] = useState([]);
  const [configuredCount, setConfiguredCount] = useState(0);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [configForm, setConfigForm] = useState({});

  const ICONS = {
    'database': Database,
    'users': Users,
    'credit-card': CreditCard,
    'message-circle': MessageCircle,
    'mail': Mail,
    'utensils': Utensils,
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.allSettled([
        fetchBitrixStatus(),
        fetchIntegrations(),
        fetchOneCDashboard()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBitrixStatus = async () => {
    try {
      const response = await axios.get(`${API}/bitrix24/sync-status`, {
        headers: getAuthHeaders()
      });
      setSyncStats(response.data);
    } catch (error) {
      console.error('Error fetching Bitrix status:', error);
    }
  };
  
  const fetchOneCDashboard = async () => {
    setOneCLoading(true);
    try {
      const response = await axios.get(`${API}/1c/dashboard`, {
        headers: getAuthHeaders()
      });
      setOneCData(response.data);
    } catch (error) {
      console.error('Error fetching 1C dashboard:', error);
    } finally {
      setOneCLoading(false);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/admin/integrations`, {
        headers: getAuthHeaders()
      });
      setIntegrations(response.data.integrations);
      setConfiguredCount(response.data.configured_count);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const handleTestBitrixConnection = async () => {
    setBitrixTesting(true);
    try {
      const response = await axios.get(`${API}/bitrix24/test`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        setBitrixStatus({ connected: true, user: response.data.user });
        toast.success(`Подключено! Пользователь: ${response.data.user?.NAME || 'OK'}`);
      } else {
        setBitrixStatus({ connected: false, error: response.data.error });
        toast.error(`Ошибка: ${response.data.error}`);
      }
    } catch (error) {
      setBitrixStatus({ connected: false, error: error.message });
      toast.error('Не удалось подключиться к Bitrix24');
    } finally {
      setBitrixTesting(false);
    }
  };

  const handleSyncClients = async () => {
    setBitrixSyncing('clients');
    try {
      const response = await axios.post(`${API}/bitrix24/sync/all-clients`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(response.data.message);
      setTimeout(fetchBitrixStatus, 3000);
    } catch (error) {
      toast.error('Ошибка синхронизации клиентов');
    } finally {
      setBitrixSyncing(null);
    }
  };

  const handleSyncOrders = async () => {
    setBitrixSyncing('orders');
    try {
      const response = await axios.post(`${API}/bitrix24/sync/all-orders`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(response.data.message);
      setTimeout(fetchBitrixStatus, 3000);
    } catch (error) {
      toast.error('Ошибка синхронизации заказов');
    } finally {
      setBitrixSyncing(null);
    }
  };

  // Integration config handlers
  const handleOpenConfig = async (integration) => {
    // For Bitrix24 and 1C - switch to their dedicated tabs
    if (integration.id === 'bitrix24') {
      setActiveTab('bitrix24');
      return;
    }
    if (integration.id === '1c') {
      setActiveTab('1c');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/admin/integrations/${integration.id}`, {
        headers: getAuthHeaders()
      });
      setSelectedIntegration(response.data);
      setConfigForm(response.data.config || {});
      setShowConfigDialog(true);
    } catch (error) {
      toast.error('Не удалось загрузить настройки');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API}/admin/integrations/${selectedIntegration.id}`, {
        name: selectedIntegration.name,
        enabled: selectedIntegration.enabled,
        settings: configForm
      }, { headers: getAuthHeaders() });
      
      toast.success('Настройки сохранены');
      setShowConfigDialog(false);
      fetchIntegrations();
    } catch (error) {
      toast.error('Не удалось сохранить настройки');
    }
  };

  const handleToggleIntegration = async (integration, enabled) => {
    // For Bitrix24 and 1C - redirect to their tabs
    if (integration.id === 'bitrix24') {
      setActiveTab('bitrix24');
      toast.info('Управление Bitrix24 доступно на вкладке "Bitrix24 CRM"');
      return;
    }
    if (integration.id === '1c') {
      setActiveTab('1c');
      toast.info('Управление 1С доступно на вкладке "1С Предприятие"');
      return;
    }
    
    try {
      await axios.put(`${API}/admin/integrations/${integration.id}`, {
        name: integration.name,
        enabled: enabled,
        settings: {}
      }, { headers: getAuthHeaders() });
      
      toast.success(enabled ? 'Интеграция включена' : 'Интеграция отключена');
      fetchIntegrations();
    } catch (error) {
      toast.error('Не удалось изменить статус');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Никогда';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'configured':
      case 'connected':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Подключено</Badge>;
      case 'disabled':
      case 'disconnected':
        return <Badge className="bg-gray-100 text-gray-800"><XCircle className="w-3 h-3 mr-1" />Отключено</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800"><AlertTriangle className="w-3 h-3 mr-1" />Не настроено</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">Загрузка интеграций...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`}>
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Назад
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Интеграции</h1>
                <p className="text-sm text-gray-500">Подключение внешних сервисов</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="bitrix24" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Bitrix24 CRM
            </TabsTrigger>
            <TabsTrigger value="1c" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              1С Предприятие
            </TabsTrigger>
            <TabsTrigger value="incoming-1c" className="flex items-center gap-2" data-testid="tab-incoming-1c">
              <Inbox className="w-4 h-4" />
              Входящие из 1С
              {oneCData?.push_back?.pending_queue > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-orange-500 text-white" data-testid="incoming-1c-badge">
                  {oneCData.push_back.pending_queue}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="others" className="flex items-center gap-2">
              <Plug className="w-4 h-4" />
              Другие интеграции
            </TabsTrigger>
          </TabsList>

          {/* Bitrix24 Tab */}
          <TabsContent value="bitrix24">
            <div className="space-y-6">
              {/* Connection Status */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        bitrixStatus?.connected ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        <Building2 className={`w-6 h-6 ${
                          bitrixStatus?.connected ? 'text-green-600' : 'text-blue-600'
                        }`} />
                      </div>
                      <div>
                        <CardTitle>Bitrix24 CRM</CardTitle>
                        <CardDescription>ainanan.bitrix24.kz</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {bitrixStatus?.connected ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Подключено
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800">
                          Не проверено
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleTestBitrixConnection}
                        disabled={bitrixTesting}
                      >
                        {bitrixTesting ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-1" />
                            Проверить
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <UserCheck className="w-5 h-5 text-blue-600" />
                        <span className="font-medium">Контакты/Компании</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        Автоматическая синхронизация клиентов при регистрации
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        B2C → Контакты
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        B2B → Компании
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <ShoppingCart className="w-5 h-5 text-emerald-600" />
                        <span className="font-medium">Сделки</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        Автоматическое создание сделок при заказе
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        Заказ → Сделка
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        Товары → Товарные позиции
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <RefreshCw className="w-5 h-5 text-purple-600" />
                        <span className="font-medium">Статусы</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        Двусторонняя синхронизация статусов
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        Магазин → Bitrix24
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        Bitrix24 → Магазин (webhook)
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sync Stats */}
              {syncStats && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Clients Sync */}
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          Синхронизация клиентов
                        </CardTitle>
                        <Button
                          size="sm"
                          onClick={handleSyncClients}
                          disabled={bitrixSyncing === 'clients'}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {bitrixSyncing === 'clients' ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          Синхронизировать всех
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Синхронизировано</span>
                          <span className="font-medium">
                            {syncStats.clients.synced} из {syncStats.clients.total}
                          </span>
                        </div>
                        <Progress 
                          value={syncStats.clients.total > 0 
                            ? (syncStats.clients.synced / syncStats.clients.total) * 100 
                            : 0
                          } 
                          className="h-2"
                        />
                        {syncStats.clients.pending > 0 && (
                          <div className="flex items-center gap-2 text-sm text-orange-600">
                            <AlertTriangle className="w-4 h-4" />
                            {syncStats.clients.pending} клиентов ожидают синхронизации
                          </div>
                        )}
                        {syncStats.clients.pending === 0 && syncStats.clients.total > 0 && (
                          <div className="flex items-center gap-2 text-sm text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            Все клиенты синхронизированы
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Orders Sync */}
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Package className="w-5 h-5 text-emerald-600" />
                          Синхронизация заказов
                        </CardTitle>
                        <Button
                          size="sm"
                          onClick={handleSyncOrders}
                          disabled={bitrixSyncing === 'orders'}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {bitrixSyncing === 'orders' ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          Синхронизировать все
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Синхронизировано</span>
                          <span className="font-medium">
                            {syncStats.orders.synced} из {syncStats.orders.total}
                          </span>
                        </div>
                        <Progress 
                          value={syncStats.orders.total > 0 
                            ? (syncStats.orders.synced / syncStats.orders.total) * 100 
                            : 0
                          } 
                          className="h-2"
                        />
                        {syncStats.orders.pending > 0 && (
                          <div className="flex items-center gap-2 text-sm text-orange-600">
                            <AlertTriangle className="w-4 h-4" />
                            {syncStats.orders.pending} заказов ожидают синхронизации
                          </div>
                        )}
                        {syncStats.orders.pending === 0 && syncStats.orders.total > 0 && (
                          <div className="flex items-center gap-2 text-sm text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            Все заказы синхронизированы
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Recent Sync Logs */}
              {syncStats?.recent_logs?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-gray-600" />
                      История синхронизации
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {syncStats.recent_logs.map((log, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {log.type === 'clients_bulk_sync' ? (
                              <Users className="w-4 h-4 text-blue-600" />
                            ) : (
                              <Package className="w-4 h-4 text-emerald-600" />
                            )}
                            <div>
                              <span className="font-medium">
                                {log.type === 'clients_bulk_sync' ? 'Клиенты' : 'Заказы'}
                              </span>
                              <span className="text-sm text-gray-500 ml-2">
                                {formatDate(log.timestamp)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-800">
                              {log.synced} синхр.
                            </Badge>
                            {log.failed > 0 && (
                              <Badge className="bg-red-100 text-red-800">
                                {log.failed} ошибок
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Webhook Info */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <ExternalLink className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-blue-800 font-medium">Webhook для обратной синхронизации</p>
                      <p className="text-blue-600 text-sm mt-1">
                        Для автоматического обновления статусов из Bitrix24, настройте исходящий webhook в Bitrix24 на URL:
                      </p>
                      <code className="block mt-2 p-2 bg-white rounded text-sm text-blue-800 font-mono">
                        {API}/bitrix24/webhook
                      </code>
                      <p className="text-blue-600 text-xs mt-2">
                        События: ONCRMDEALUPDATE (обновление сделки)
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>


          {/* 1C Tab */}
          <TabsContent value="1c">
            {oneCLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : oneCData ? (
              <div className="space-y-6">
                {/* Connection Status */}
                <Card className={oneCData.connection.configured ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Database className="w-5 h-5" />
                        Подключение к 1С
                      </CardTitle>
                      <Badge className={oneCData.connection.configured ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {oneCData.connection.configured ? 'Подключено' : 'Не подключено'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Пользователь API</p>
                        <p className="font-medium">{oneCData.connection.username || '—'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Активные сессии</p>
                        <p className="font-medium">{oneCData.connection.active_sessions}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Последняя синхронизация</p>
                        <p className="font-medium">
                          {oneCData.orders.last_synced_at 
                            ? new Date(oneCData.orders.last_synced_at).toLocaleString('ru')
                            : 'Нет данных'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <ShoppingCart className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Заказов отправлено</p>
                          <p className="text-2xl font-bold text-gray-900">{oneCData.orders.synced}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          oneCData.orders.pending > 0 ? 'bg-orange-100' : 'bg-green-100'
                        }`}>
                          <Clock className={`w-5 h-5 ${oneCData.orders.pending > 0 ? 'text-orange-600' : 'text-green-600'}`} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Ожидают отправки</p>
                          <p className="text-2xl font-bold text-gray-900">{oneCData.orders.pending}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Package className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Товаров с кодом 1С</p>
                          <p className="text-2xl font-bold text-gray-900">{oneCData.products.with_code_1c}/{oneCData.products.total}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Товаров с NTIN</p>
                          <p className="text-2xl font-bold text-gray-900">{oneCData.products.with_ntin}/{oneCData.products.total}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Products Sync */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Синхронизация товаров</CardTitle>
                    <CardDescription>Привязка товаров сайта к номенклатуре 1С</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          <span className="text-sm">Код 1С заполнен</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{oneCData.products.with_code_1c}</span>
                          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500 rounded-full" style={{ width: `${(oneCData.products.with_code_1c / Math.max(oneCData.products.total, 1)) * 100}%` }} />
                          </div>
                          <span className="text-sm text-gray-500">{Math.round((oneCData.products.with_code_1c / Math.max(oneCData.products.total, 1)) * 100)}%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                          <span className="text-sm">NTIN (НКТ) заполнен</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{oneCData.products.with_ntin}</span>
                          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(oneCData.products.with_ntin / Math.max(oneCData.products.total, 1)) * 100}%` }} />
                          </div>
                          <span className="text-sm text-gray-500">{Math.round((oneCData.products.with_ntin / Math.max(oneCData.products.total, 1)) * 100)}%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-purple-500" />
                          <span className="text-sm">Артикул заполнен</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{oneCData.products.with_article}</span>
                          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full" style={{ width: `${(oneCData.products.with_article / Math.max(oneCData.products.total, 1)) * 100}%` }} />
                          </div>
                          <span className="text-sm text-gray-500">{Math.round((oneCData.products.with_article / Math.max(oneCData.products.total, 1)) * 100)}%</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Orders Sync History */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">История синхронизации заказов</CardTitle>
                        <CardDescription>Последние заказы, отправленные в 1С</CardDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={fetchOneCDashboard}>
                        <RefreshCw className="w-4 h-4 mr-1" /> Обновить
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {oneCData.sync_history.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="px-3 py-2 text-left text-gray-600">Заказ</th>
                              <th className="px-3 py-2 text-left text-gray-600">Дата синхронизации</th>
                              <th className="px-3 py-2 text-right text-gray-600">Сумма</th>
                              <th className="px-3 py-2 text-center text-gray-600">Позиций</th>
                              <th className="px-3 py-2 text-center text-gray-600">Статус</th>
                            </tr>
                          </thead>
                          <tbody>
                            {oneCData.sync_history.map((item, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-3 py-2 font-medium">#{item.order_number}</td>
                                <td className="px-3 py-2 text-gray-600">
                                  {item.synced_at ? new Date(item.synced_at).toLocaleString('ru') : '—'}
                                </td>
                                <td className="px-3 py-2 text-right">{item.total_amount.toLocaleString()} ₸</td>
                                <td className="px-3 py-2 text-center">{item.items_count}</td>
                                <td className="px-3 py-2 text-center">
                                  <Badge className="bg-green-100 text-green-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />Отправлен
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Clock className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                        <p>Нет данных о синхронизации</p>
                        <p className="text-sm">Заказы будут появляться здесь после отправки в 1С</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="border-red-200">
                <CardContent className="py-8 text-center">
                  <XCircle className="w-10 h-10 mx-auto mb-2 text-red-400" />
                  <p className="text-red-600">Не удалось загрузить данные 1С</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={fetchOneCDashboard}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Повторить
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Incoming from 1C Tab */}
          <TabsContent value="incoming-1c">
            <IncomingOrders1C />
          </TabsContent>

          {/* Other Integrations Tab */}
          <TabsContent value="others">
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-500">Всего интеграций</div>
                        <div className="text-2xl font-bold">{integrations.length}</div>
                      </div>
                      <Plug className="w-8 h-8 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-500">Настроено</div>
                        <div className="text-2xl font-bold text-green-600">{configuredCount}</div>
                      </div>
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-500">Требует настройки</div>
                        <div className="text-2xl font-bold text-orange-600">{integrations.length - configuredCount}</div>
                      </div>
                      <AlertTriangle className="w-8 h-8 text-orange-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Info Banner - only show if there are disconnected integrations */}
              {integrations.length - configuredCount > 0 && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-blue-800 font-medium">Требуют настройки</p>
                        <p className="text-blue-600 text-sm">
                          {integrations.length - configuredCount} из {integrations.length} интеграций ещё не подключены. Настройте их для полноценной работы.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Integrations Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {integrations.map((integration) => {
                  const IconComponent = ICONS[integration.icon] || Database;
                  return (
                    <Card key={integration.id} data-testid={`integration-card-${integration.id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              integration.status === 'configured' || integration.status === 'connected' ? 'bg-green-100' : 'bg-gray-100'
                            }`}>
                              <IconComponent className={`w-6 h-6 ${
                                integration.status === 'configured' || integration.status === 'connected' ? 'text-green-600' : 'text-gray-400'
                              }`} />
                            </div>
                            <div>
                              <CardTitle className="text-base">{integration.name}</CardTitle>
                              {getStatusBadge(integration.status)}
                            </div>
                          </div>
                          <Switch
                            checked={integration.enabled}
                            onCheckedChange={(checked) => handleToggleIntegration(integration, checked)}
                            data-testid={`toggle-${integration.id}`}
                          />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-4">{integration.description}</p>
                        
                        <div className="space-y-2 mb-4">
                          {integration.features?.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-3 h-3 text-emerald-500" />
                              <span className="text-gray-600">{feature}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleOpenConfig(integration)}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Настроить
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Настройка: {selectedIntegration?.name}</DialogTitle>
          </DialogHeader>
          
          {selectedIntegration && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">{selectedIntegration.description}</p>
              
              {/* Config Fields */}
              <div className="space-y-4">
                {selectedIntegration.config_fields?.map((field) => (
                  <div key={field.key}>
                    <Label>{field.label}</Label>
                    <Input
                      type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                      value={configForm[field.key] || ''}
                      onChange={(e) => setConfigForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={`Введите ${field.label.toLowerCase()}`}
                    />
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowConfigDialog(false)}
                  className="flex-1"
                >
                  Отмена
                </Button>
                <Button
                  onClick={handleSaveConfig}
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                >
                  Сохранить
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminIntegrations;
